import React, { useRef, useEffect, useCallback } from 'react';
import { Box } from '@mui/joy';
import { getDocumentDisplayName } from '../helpers/documentNames';
import { createDocumentMentionRegex } from '../helpers/documentMentions';

// Marker pattern for inline quotes: ⟦id⟧
const QUOTE_MARKER_REGEX = /⟦(\d+)⟧/g;
const createMarker = (id) => `⟦${id}⟧`;

// Pattern for document mentions: @document:path/to/file.pdf
const DOCUMENT_MENTION_REGEX = createDocumentMentionRegex('g');

// Patterns for task and policy mentions
const TASK_MENTION_REGEX = /@task:([^\s]+)/g;
const POLICY_MENTION_REGEX = /@policy:([^\s]+)/g;

/**
 * RichChatInput - A stable input with inline quote chips embedded in text.
 * 
 * Uses a contenteditable approach with careful DOM management.
 * Quotes are stored with IDs and inserted as markers in the text.
 * 
 * Props:
 * - value: The current text value (contains markers like ⟦0⟧ for quotes)
 * - onChange: Called with (newValue, cursorPosition) when text changes
 * - onKeyDown: Keyboard event handler
 * - quotes: Array of quote objects {id, text, source}
 * - onRemoveQuote: Called with quote id when removing a quote chip
 * - onAddQuoteAtCursor: Callback to get cursor position for adding quotes
 * - placeholder: Placeholder text when empty
 * - disabled: Whether input is disabled
 * - inputRef: Forwarded ref to access the editor
 * - minRows: Minimum height in rows (default 1)
 * - maxRows: Maximum height in rows (default 4)
 */
const RichChatInput = ({
  value = '',
  onChange,
  onKeyDown,
  quotes = [],
  onRemoveQuote,
  onOpenDocument,
  onOpenPolicy,
  placeholder = 'Type a message...',
  disabled = false,
  inputRef: externalRef,
  minRows = 1,
  maxRows = 4,
  cursorPositionRef,
}) => {
  const internalRef = useRef(null);
  const editorRef = externalRef || internalRef;
  const isUpdatingRef = useRef(false);
  const lastValueRef = useRef(null);

  // Calculate heights
  const lineHeight = 21;
  const padding = 16;
  const minHeight = minRows * lineHeight + padding;
  const maxHeight = maxRows * lineHeight + padding;

  // Build quotes map for quick lookup
  const quotesMap = React.useMemo(() => {
    const map = new Map();
    quotes.forEach((q) => map.set(q.id, q));
    return map;
  }, [quotes]);

  // Parse value into segments (text, quote markers, and document mentions)
  const segments = React.useMemo(() => {
    const result = [];

    // Combined pattern for quotes, documents, tasks, and policies
    const combinedPattern = new RegExp(
      `(${QUOTE_MARKER_REGEX.source})|(${DOCUMENT_MENTION_REGEX.source})|(${TASK_MENTION_REGEX.source})|(${POLICY_MENTION_REGEX.source})`,
      'g'
    );

    let lastIndex = 0;
    let match;

    while ((match = combinedPattern.exec(value)) !== null) {
      // Add text before match
      if (match.index > lastIndex) {
        result.push({ type: 'text', content: value.slice(lastIndex, match.index) });
      }

      if (match[1]) {
        // Quote marker match: ⟦id⟧
        const quoteId = parseInt(match[2], 10);
        const quote = quotesMap.get(quoteId);
        if (quote) {
          result.push({ type: 'quote', quote, id: quoteId });
        }
      } else if (match[3]) {
        // Document mention match: @document:path
        const fullMatch = match[3];
        const filePath = match[4];
        const fileName = getDocumentDisplayName(filePath) || filePath;
        result.push({
          type: 'document',
          fullMatch,
          filePath,
          fileName
        });
      } else if (match[5]) {
        // Task mention match: @task:id
        const fullMatch = match[5];
        const taskId = match[6];
        result.push({
          type: 'task',
          fullMatch,
          taskId
        });
      } else if (match[7]) {
        // Policy mention match: @policy:id
        const fullMatch = match[7];
        const policyId = match[8];
        result.push({
          type: 'policy',
          fullMatch,
          policyId
        });
      }

      lastIndex = match.index + match[0].length;
    }

    // Add remaining text
    if (lastIndex < value.length) {
      result.push({ type: 'text', content: value.slice(lastIndex) });
    }

    // If empty, add empty text segment for cursor
    if (result.length === 0) {
      result.push({ type: 'text', content: '' });
    }

    return result;
  }, [value, quotesMap]);

  // Get current cursor position in terms of the value string
  const getCursorPosition = useCallback(() => {
    const editor = editorRef.current;
    if (!editor) return 0;

    const selection = window.getSelection();
    if (!selection.rangeCount) return 0;

    const range = selection.getRangeAt(0);

    // Create a range from start to cursor
    const preCaretRange = document.createRange();
    preCaretRange.selectNodeContents(editor);
    preCaretRange.setEnd(range.startContainer, range.startOffset);

    // Clone contents and calculate position
    const fragment = preCaretRange.cloneContents();
    const tempDiv = document.createElement('div');
    tempDiv.appendChild(fragment);

    // Convert quote chips back to markers for accurate position
    const quoteChips = tempDiv.querySelectorAll('[data-quote-id]');
    quoteChips.forEach((chip) => {
      const id = chip.getAttribute('data-quote-id');
      chip.replaceWith(createMarker(id));
    });

    // Convert document chips back to mentions for accurate position
    const docChips = tempDiv.querySelectorAll('[data-document-mention]');
    docChips.forEach((chip) => {
      const mention = chip.getAttribute('data-document-mention');
      chip.replaceWith(mention);
    });

    // Convert task chips back to mentions for accurate position
    const taskChips = tempDiv.querySelectorAll('[data-task-mention]');
    taskChips.forEach((chip) => {
      const mention = chip.getAttribute('data-task-mention');
      chip.replaceWith(mention);
    });

    // Convert policy chips back to mentions for accurate position
    const policyChips = tempDiv.querySelectorAll('[data-policy-mention]');
    policyChips.forEach((chip) => {
      const mention = chip.getAttribute('data-policy-mention');
      chip.replaceWith(mention);
    });

    // Strip zero-width spaces for accurate position
    return tempDiv.textContent.replace(/\u200B/g, '').length;
  }, [editorRef]);

  // Set cursor position
  const setCursorAtEnd = useCallback(() => {
    const editor = editorRef.current;
    if (!editor) return;

    const range = document.createRange();
    const selection = window.getSelection();

    // Find the last text node
    const walker = document.createTreeWalker(editor, NodeFilter.SHOW_TEXT, null);
    let lastTextNode = null;
    let node;
    while ((node = walker.nextNode())) {
      lastTextNode = node;
    }

    if (lastTextNode) {
      range.setStart(lastTextNode, lastTextNode.textContent.length);
      range.collapse(true);
    } else {
      range.selectNodeContents(editor);
      range.collapse(false);
    }

    selection.removeAllRanges();
    selection.addRange(range);
  }, [editorRef]);

  // Handle input changes
  const handleInput = useCallback(() => {
    if (isUpdatingRef.current) return;

    const editor = editorRef.current;
    if (!editor) return;

    // Extract text content, converting chips back to markers/mentions
    let newValue = '';
    const processNode = (node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        newValue += node.textContent;
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        const quoteId = node.getAttribute?.('data-quote-id');
        const documentMention = node.getAttribute?.('data-document-mention');
        const taskMention = node.getAttribute?.('data-task-mention');
        const policyMention = node.getAttribute?.('data-policy-mention');

        if (quoteId) {
          newValue += createMarker(quoteId);
        } else if (documentMention) {
          // Preserve the full document mention
          newValue += documentMention;
        } else if (taskMention) {
          newValue += taskMention;
        } else if (policyMention) {
          newValue += policyMention;
        } else {
          // Handle BR and DIV (newlines)
          if (node.tagName === 'BR') {
            newValue += '\n';
          } else if (node.tagName === 'DIV' && newValue.length > 0 && !newValue.endsWith('\n')) {
            newValue += '\n';
          }
          node.childNodes.forEach(processNode);
        }
      }
    };

    editor.childNodes.forEach(processNode);

    // Clean up zero-width spaces and trailing newlines from contenteditable quirks
    newValue = newValue.replace(/\u200B/g, '').replace(/\n$/, '');

    const cursorPos = getCursorPosition();

    // Update cursor position ref for quote insertion
    if (cursorPositionRef) {
      cursorPositionRef.current = cursorPos;
    }

    if (newValue !== lastValueRef.current) {
      lastValueRef.current = newValue;
      onChange?.(newValue, cursorPos);
    }
  }, [editorRef, getCursorPosition, onChange, cursorPositionRef]);

  // Handle keyboard events
  const handleKeyDown = useCallback((e) => {
    // Update cursor position before handling
    if (cursorPositionRef) {
      cursorPositionRef.current = getCursorPosition();
    }

    // Handle Enter for line breaks vs send
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      onKeyDown?.(e);
      return;
    }

    if (e.key === 'Enter' && e.shiftKey) {
      // Allow default for line break
      return;
    }

    onKeyDown?.(e);
  }, [onKeyDown, getCursorPosition, cursorPositionRef]);

  // Handle paste - strip formatting
  const handlePaste = useCallback((e) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text/plain');
    document.execCommand('insertText', false, text);
  }, []);

  // Handle focus - place cursor at end if no selection exists
  const handleFocus = useCallback(() => {
    setTimeout(() => {
      const selection = window.getSelection();
      // If clicking on the editor with no existing selection, place cursor at end
      if (!selection.rangeCount || selection.isCollapsed) {
        setCursorAtEnd();
      }
      // Update cursor position
      if (cursorPositionRef) {
        cursorPositionRef.current = getCursorPosition();
      }
    }, 0);
  }, [getCursorPosition, cursorPositionRef, setCursorAtEnd]);

  // Handle click to update cursor position
  const handleClick = useCallback(() => {
    if (cursorPositionRef) {
      setTimeout(() => {
        cursorPositionRef.current = getCursorPosition();
      }, 0);
    }
  }, [getCursorPosition, cursorPositionRef]);

  // Remove quote and its marker from value
  const handleRemoveQuote = useCallback((quoteId) => {
    const marker = createMarker(quoteId);
    const newValue = value.replace(marker, '');
    lastValueRef.current = newValue;
    onChange?.(newValue, getCursorPosition());
    onRemoveQuote?.(quoteId);
  }, [value, onChange, onRemoveQuote, getCursorPosition]);

  // Render content when value changes
  useEffect(() => {
    const editor = editorRef.current;
    if (!editor || isUpdatingRef.current) return;

    // Only update if value actually changed
    if (value === lastValueRef.current) return;
    lastValueRef.current = value;

    isUpdatingRef.current = true;

    // Clear and rebuild content
    editor.innerHTML = '';

    segments.forEach((segment) => {
      if (segment.type === 'text') {
        // Split by newlines to handle multi-line
        const lines = segment.content.split('\n');
        lines.forEach((line, idx) => {
          if (idx > 0) {
            editor.appendChild(document.createElement('br'));
          }
          if (line) {
            editor.appendChild(document.createTextNode(line));
          }
        });
      } else if (segment.type === 'quote') {
        const chip = document.createElement('span');
        chip.setAttribute('data-quote-id', segment.id.toString());
        chip.setAttribute('contenteditable', 'false');
        chip.style.cssText = `
          display: inline-flex;
          align-items: center;
          gap: 4px;
          padding: 2px 8px;
          margin: 0 2px;
          border-radius: 12px;
          font-size: 0.75rem;
          font-weight: 500;
          user-select: none;
          vertical-align: middle;
          cursor: default;
        `;

        // Set colors based on source
        const colors = {
          user: { bg: '#e0e0e0', text: '#424242' },
          assistant: { bg: '#e3f2fd', text: '#1565c0' },
          policy: { bg: '#e8f5e9', text: '#2e7d32' },
          file: { bg: '#fff3e0', text: '#ef6c00' },
        };
        const colorScheme = colors[segment.quote.source] || colors.user;
        chip.style.backgroundColor = colorScheme.bg;
        chip.style.color = colorScheme.text;

        // Icon (simple text representation)
        const icons = {
          user: '👤',
          assistant: '🤖',
          policy: '📋',
          file: '📄',
        };

        // Label
        const label = document.createElement('span');
        label.textContent = `${icons[segment.quote.source] || '📝'} ${segment.quote.source}`;
        chip.appendChild(label);

        // Close button
        const closeBtn = document.createElement('span');
        closeBtn.textContent = '×';
        closeBtn.style.cssText = `
          margin-left: 4px;
          cursor: pointer;
          font-weight: bold;
          opacity: 0.7;
        `;
        closeBtn.addEventListener('mousedown', (e) => {
          e.preventDefault();
          e.stopPropagation();
        });
        closeBtn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          handleRemoveQuote(segment.id);
        });
        chip.appendChild(closeBtn);

        editor.appendChild(chip);

        // Add zero-width space after chip to allow cursor placement
        editor.appendChild(document.createTextNode('\u200B'));
      } else if (segment.type === 'document') {
        // Create document mention chip
        const chip = document.createElement('span');
        chip.setAttribute('data-document-mention', segment.fullMatch);
        chip.setAttribute('data-document-path', segment.filePath);
        chip.setAttribute('data-document-name', segment.fileName);
        chip.setAttribute('contenteditable', 'false');
        chip.style.cssText = `
          display: inline-flex;
          align-items: center;
          gap: 4px;
          padding: 2px 8px;
          margin: 0 2px;
          border-radius: 12px;
          font-size: 0.75rem;
          font-weight: 500;
          user-select: none;
          vertical-align: middle;
          cursor: pointer;
          background-color: #e3f2fd;
          color: #1565c0;
          transition: background-color 0.15s ease;
        `;

        // Add hover effect
        chip.addEventListener('mouseenter', () => {
          chip.style.backgroundColor = '#bbdefb';
        });
        chip.addEventListener('mouseleave', () => {
          chip.style.backgroundColor = '#e3f2fd';
        });

        // Add click handler to open document
        chip.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          if (onOpenDocument) {
            const filePath = chip.getAttribute('data-document-path');
            const fileName = chip.getAttribute('data-document-name');
            onOpenDocument({
              id: filePath,
              name: fileName,
              path: filePath,
              type: fileName?.split('.').pop()?.toLowerCase(),
            });
          }
        });

        // Document icon
        const icon = document.createElement('span');
        icon.textContent = '📄';
        chip.appendChild(icon);

        // File name label
        const label = document.createElement('span');
        label.textContent = segment.fileName;
        label.style.cssText = `
          max-width: 150px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        `;
        chip.appendChild(label);

        editor.appendChild(chip);

        // Add zero-width space after chip to allow cursor placement
        editor.appendChild(document.createTextNode('\u200B'));
      } else if (segment.type === 'task' || segment.type === 'policy') {
        const isTask = segment.type === 'task';
        const id = isTask ? segment.taskId : segment.policyId;
        const color = isTask ? '#f3e5f5' : '#e8f5e9';
        const textColor = isTask ? '#7b1fa2' : '#2e7d32';
        const iconSymbol = isTask ? '📝' : '📋';

        const chip = document.createElement('span');
        chip.setAttribute(`data-${segment.type}-mention`, segment.fullMatch);
        chip.setAttribute(`data-${segment.type}-id`, id);
        chip.setAttribute('contenteditable', 'false');
        chip.style.cssText = `
          display: inline-flex;
          align-items: center;
          gap: 4px;
          padding: 2px 8px;
          margin: 0 2px;
          border-radius: 12px;
          font-size: 0.75rem;
          font-weight: 500;
          user-select: none;
          vertical-align: middle;
          cursor: ${!isTask && onOpenPolicy ? 'pointer' : 'default'};
          background-color: ${color};
          color: ${textColor};
        `;

        if (!isTask && onOpenPolicy) {
          chip.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            onOpenPolicy(id);
          });
        }

        const icon = document.createElement('span');
        icon.textContent = iconSymbol;
        chip.appendChild(icon);

        const label = document.createElement('span');
        label.textContent = `${isTask ? 'Task' : 'Policy'} ${id}`;
        chip.appendChild(label);

        editor.appendChild(chip);

        // Add zero-width space after chip to allow cursor placement
        editor.appendChild(document.createTextNode('\u200B'));
      }
    });

    // If editor is empty, ensure there's a BR for cursor
    if (editor.innerHTML === '') {
      editor.appendChild(document.createElement('br'));
    }

    // Ensure there's always a text node at the end for cursor placement
    const lastChild = editor.lastChild;
    if (lastChild && lastChild.nodeType !== Node.TEXT_NODE) {
      editor.appendChild(document.createTextNode('\u200B'));
    }

    isUpdatingRef.current = false;

    // Restore cursor at end (simplified - full position restoration is complex)
    if (document.activeElement === editor) {
      setCursorAtEnd();
    }
  }, [value, segments, editorRef, getCursorPosition, setCursorAtEnd, handleRemoveQuote, onOpenDocument, onOpenPolicy]);

  const isEmpty = !value && quotes.length === 0;

  return (
    <Box
      ref={editorRef}
      contentEditable={!disabled}
      suppressContentEditableWarning
      onInput={handleInput}
      onKeyDown={handleKeyDown}
      onPaste={handlePaste}
      onFocus={handleFocus}
      onClick={handleClick}
      role="textbox"
      aria-multiline="true"
      aria-placeholder={placeholder}
      tabIndex={disabled ? -1 : 0}
      data-placeholder={isEmpty ? placeholder : undefined}
      sx={{
        width: '100%',
        minHeight: `${minHeight}px`,
        maxHeight: `${maxHeight}px`,
        overflowY: 'auto',
        padding: '8px 0',
        fontSize: '1rem',
        lineHeight: 1.6,
        fontFamily: 'inherit',
        border: 'none',
        bgcolor: 'transparent',
        color: disabled ? 'neutral.plainDisabledColor' : 'text.primary',
        cursor: disabled ? 'not-allowed' : 'text',
        outline: 'none',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',

        // Placeholder styling
        '&:empty::before, &[data-placeholder]:empty::before': {
          content: `attr(data-placeholder)`,
          color: 'neutral.400',
          pointerEvents: 'none',
        },
      }}
    />
  );
};

// Helper to create a quote marker for insertion
RichChatInput.createMarker = createMarker;

export default RichChatInput;
